<template>
  <div class="tender-bidders">
    <div class="tender-bidders__header">
      <div class="title">
        {{ $t('tender-details.bidders.title') }}
      </div>
    </div>
    <div class="tender-bidders__content">
      <div class="tender-bidders__section">
        <div class="tender-bidders__results">
          <div class="tender-bidders__section-awards-title">
            <div class="tender-bidders__subtitle">
              {{ sectionTitle('results', biTender ? biTender.awarded_to.length : 0) }}
            </div>
            <div class="tender-bidders__award--total">
              <div
                v-for="currency of Object.keys(awardsTotal)"
                :key="currency"
                class="tender-bidders__award--amount-total"
              >
                <span class="tender-bidders__award--amount-currency-total number-monospace">{{ currency }}</span>
                {{ formatNumToCurrency(awardsTotal[currency], 0) }}
              </div>
            </div>
          </div>
          <BaseCircularLoadingBar v-if="pending && !biTender" :size="14" :width="2" color="accent" />
          <div v-else-if="tenderError" class="tender-bidders__unavailable">
            {{ $t('tender-details.bidders.unavailable') }}
          </div>
          <div v-else-if="biTender">
            <!-- awards do not have lot info -->
            <BIAwardsList
              size="full"
              :tenderId="tender.id"
              :source="biTender.source"
              :awards="biTender.awarded_to"
              :hasWinner="biTender.awarded_to && biTender.awarded_to.length > 0"
              @add-competitor="onAddCompetitorClicked"
              @edit-competitor="onEditCompetitorClicked"
              @remove-winner="onRemoveWinnerClicked"
              @action-succeeded="onRefresh"
            />
          </div>
        </div>
      </div>
      <div class="tender-bidders__section">
        <div class="tender-bidders__results">
          <div class="tender-bidders__title">
            <div class="tender-bidders__subtitle">
              {{ sectionTitle('bidders', biTender ? biTender.bids.length : 0) }}
            </div>
          </div>
          <BaseCircularLoadingBar v-if="pending && !biTender" :size="14" :width="2" color="accent" />
          <div v-else-if="tenderError" class="tender-bidders__unavailable">
            {{ $t('tender-details.bidders.unavailable') }}
          </div>
          <div v-else-if="biTender">
            <!-- no bids -->
            <div v-if="!biTender.bids || biTender.bids.length === 0" class="tender-bidders__empty">
              {{ $t('tender-details.bidders.empty') }}
            </div>
            <!-- bids with lots -->
            <div v-for="(lot, index) in lotsBidded" :key="index" class="tender-bidders__lot">
              <span class="tender-bidders__lot-title">{{ lot.title }}</span>
              <BIBidsList
                size="full"
                :tenderId="tender.id"
                :source="biTender.source"
                :bids="lot.bids"
                :hasWinner="biTender.awarded_to && biTender.awarded_to.length > 0"
                @add-competitor="onAddCompetitorClicked"
                @edit-competitor="onEditCompetitorClicked"
                @add-winner="onAddWinnerClicked"
                @action-succeeded="onRefresh"
              />
            </div>
            <!-- bids without lots -->
            <BIBidsList
              v-if="lotsBidded.length == 0"
              size="full"
              :tenderId="tender.id"
              :source="biTender.source"
              :bids="biTender.bids"
              :hasWinner="biTender.awarded_to && biTender.awarded_to.length > 0"
              @add-competitor="onAddCompetitorClicked"
              @edit-competitor="onEditCompetitorClicked"
              @add-winner="onAddWinnerClicked"
              @action-succeeded="onRefresh"
            />
          </div>
        </div>
      </div>
      <div class="tender-bidders__section" v-if="tender && tender.source && tender.source.id">
        <TenderPotentialBidders :tenderSourceId="tender.source.id" @action-succeeded="onRefresh" />
      </div>
    </div>
    <AddCompetitorDialog ref="addCompetitorDialog" @action-succeeded="onRefresh" />
    <EditCompetitorDialog ref="editCompetitorDialog" @action-succeeded="onRefresh" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import AddCompetitorDialog from '@/views/business-intelligence/AddCompetitorDialog.vue';
import EditCompetitorDialog from '@/views/business-intelligence/EditCompetitorDialog.vue';
import BIBidsList from '@/components/page-business-intelligence/BIBidsList.vue';
import BIAwardsList from '@/components/page-business-intelligence/BIAwardsList.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar.vue';
import TenderPotentialBidders from '@/components/page-tender-details/TenderPotentialBidders.vue';
import { formatNumToCurrency } from '@/util/currency-util';

export default {
  name: 'TenderBidders',
  components: {
    BaseCircularLoadingBar,
    BIBidsList,
    BIAwardsList,
    TenderPotentialBidders,
    AddCompetitorDialog,
    EditCompetitorDialog,
  },
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
    tenantBids: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  computed: {
    ...mapGetters('tender', ['tender']),
    ...mapGetters('biDashboard', { pending: 'tenderPending', tenderError: 'tenderError', biTender: 'tender' }),
    lotsBidded() {
      return this.biTender && this.biTender.lots
        ? this.biTender.lots.map((lot) => {
            const lotBids = this.biTender.bids.filter((bid) => bid.related_lots && bid.related_lots.includes(lot.id));
            return { ...lot, bids: lotBids };
          })
        : [];
    },
    awardsTotal() {
      let awardsByCurrency = {};
      this.tender.awards.forEach((award) => {
        if (!award || !award.value) {
          return;
        }
        if (awardsByCurrency[award.value.currency]) {
          const amount = award.value.amount;
          awardsByCurrency[award.value.currency] = awardsByCurrency[award.value.currency] + amount;
        } else {
          awardsByCurrency[award.value.currency] = award.value.amount;
        }
      });
      return awardsByCurrency;
    },
  },
  methods: {
    ...mapActions('biTenant', ['createAward', 'deleteAward']),
    formatNumToCurrency,
    sectionTitle(key, count) {
      // No count when the BI data could not be loaded: "(0)" would read as "nobody bid".
      return this.tenderError
        ? this.$t(`tender-details.bidders.${key}-title`)
        : this.$t(`tender-details.bidders.${key}`, { count });
    },
    onAddCompetitorClicked(name) {
      this.$refs.addCompetitorDialog.open(name);
    },
    onEditCompetitorClicked(competitor, name) {
      this.$refs.editCompetitorDialog.open(competitor, name);
    },
    onAddWinnerClicked(tenderId, supplierName, amount) {
      this.createAward({
        tenderId,
        supplierName: supplierName,
        amount: amount || 0,
      })
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.create-success'));
          this.onRefresh();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onRemoveWinnerClicked(id) {
      this.deleteAward(id)
        .then(() => {
          this.$toast.success(this.$t('page.business-intelligence.manage-dialog.awards.delete-success'));
          this.onRefresh();
        })
        .catch((error) => {
          this.$toast.error(error);
        });
    },
    onRefresh() {
      this.$emit('action-succeeded');
    },
  },
};
</script>

<style lang="scss">
.tender-bidders {
  padding: 2rem;
  background: $color-white;
  margin-bottom: 1rem;
  border-radius: $border-radius-root;
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__content {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    &-content {
      margin-top: 16px;
    }
  }

  &__section {
    &-awards-title {
      display: flex;

      > :first-child {
        flex-grow: 1;
      }
    }
  }

  &__lot {
    display: flex;
    flex-direction: column;
    background: rgba($color-neutral-pale, 0.7);
    padding: 8px;
    gap: 8px;
    border-radius: $border-radius-medium;

    &-title {
      font-size: $font-size-small;
      font-weight: 600;
    }
  }

  &__empty,
  &__unavailable {
    margin-top: 8px;
    color: $color-neutral;
    font-size: $font-size-small;
  }

  &__results {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__title {
    justify-content: space-between;
    display: flex;
  }

  &__subtitle {
    font-weight: 800;
    font-size: 14px;
  }
}
</style>
